import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AlertBlockComponent } from '../../../shared/alert-block/alert-block.component';
import { AvatarBlockModule } from '../../../shared/avatar-block/avatar-block.module';
import { EmptyStateModule } from '../../../shared/empty-state/empty-state.module';
import { ButtonSpacerDirective } from '../../../shared/directives/button.directive';
import { FlexSpacerDirective } from '../../../shared/directives/flex-spacer.directive';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { ApplicationHttpClient } from '../../../../shared/http/application-http-client';
import { Routes } from '../../../../../config/routes';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';

export const VERIFY_DIALOG_MODES = {
    VERIFY: 'verify',
    SUBMITTED: 'submitted',
    IN_PROGRESS: 'in-progress',
    REQUEST_CAPABILITY: 'request-capability',
} as const;

export type VerifyAccountDialogMode = typeof VERIFY_DIALOG_MODES[keyof typeof VERIFY_DIALOG_MODES];

export const CAPABILITY_TYPES = {
    FUND_DEPOSITS: 'fund-deposits',
    EXTERNAL_PAYOUTS: 'external-payouts',
} as const;

export type CapabilityType = typeof CAPABILITY_TYPES[keyof typeof CAPABILITY_TYPES];

export interface VerifyAccountDialogData {
    mode: VerifyAccountDialogMode;
    capabilityType?: CapabilityType;
}

@UntilDestroy()
@Component({
    selector: 'app-verify-account-dialog',
    templateUrl: './verify-account-dialog.component.html',
    styleUrls: ['./verify-account-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        AlertBlockComponent,
        AvatarBlockModule,
        EmptyStateModule,
        ButtonSpacerDirective,
        FlexSpacerDirective,
    ],
})
export class VerifyAccountDialogComponent {
    static readonly width = MatDialogSizes.SM;

    readonly modes = VERIFY_DIALOG_MODES;
    readonly capabilities = CAPABILITY_TYPES;

    mode: VerifyAccountDialogMode;
    capabilityType: CapabilityType | null;
    isSubmitting = false;

    constructor(
        private _dialogRef: MatDialogRef<VerifyAccountDialogComponent>,
        private _http: ApplicationHttpClient,
        private _cdr: ChangeDetectorRef,
        private _growler: GrowlerService,
        @Inject(MAT_DIALOG_DATA) data: VerifyAccountDialogData,
    ) {
        this.mode = data?.mode ?? 'verify';
        this.capabilityType = data?.capabilityType ?? null;
    }

    get dialogTitle(): string {
        if (this.mode === VERIFY_DIALOG_MODES.SUBMITTED) {
            return 'Request Submitted';
        }
        if (this.mode === VERIFY_DIALOG_MODES.IN_PROGRESS) {
            return 'Verification In Progress';
        }
        if (this.mode === VERIFY_DIALOG_MODES.REQUEST_CAPABILITY) {
            return this.capabilityType === CAPABILITY_TYPES.FUND_DEPOSITS
                ? 'Request Fund Deposits'
                : 'Request External Payouts & Spend Cards';
        }
        return 'Verify your account to move money';
    }

    onRequestEarlyVerification(): void {
        if (this.isSubmitting) {
            return;
        }

        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return;
        }

        this.isSubmitting = true;
        this._cdr.markForCheck();

        this._http.post(
            Routes.MakeLegFiCoreUrl(Routes.TreasuryModule.RequestExpeditedApproval(jwt.orgId)),
            null,
        ).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.mode = VERIFY_DIALOG_MODES.SUBMITTED;
                this.isSubmitting = false;
                this._cdr.markForCheck();
            },
            error: (err) => {
                this._growler.fromErrorResponse(err);
                this.isSubmitting = false;
                this._cdr.markForCheck();
            },
        });
    }

    onClose(): void {
        this._dialogRef.close();
    }
}
